import React from "react";
import { Eye, PackageCheck } from "lucide-react";
import { ActionButton, Card, SelectLike, StatusPill } from "./inventory-ui";
import {
  formatNumber,
  getReadableWarehouseOrderId,
} from "../utils/inventory.utils";

function getOrderItemKey(item) {
  return String(
    item?.id ||
      item?.orderItemId ||
      [item?.productId, item?.productVariantId || "default"].filter(Boolean).join(":")
  );
}

function getVariantLabel(item = {}) {
  return (
    item.variantName ||
    item.packLabel ||
    [item.weightValue, item.weightUnitSymbol].filter(Boolean).join(" ") ||
    item.variantSku ||
    ""
  );
}

export default function WarehouseOrdersTab({
  visibleOrders,
  ordersLoading,
  ordersError,
  selectedOrderId,
  setSelectedOrderId,
  selectedOrder,
  warehouseMap,
  decisions,
  updateDecision,
  submitDecisions,
  savingDecision,
  pagination,
}) {
  return (
    <div className="grid grid-cols-1 items-start gap-6 2xl:grid-cols-[1.05fr_1.2fr]">
      <Card className="flex h-[760px] flex-col overflow-visible">
        <div className="flex items-center justify-between border-b border-[#f4d8dc] px-5 py-4">
          <div>
            <div className="text-[18px] font-bold text-slate-800">
              Warehouse Order Requests
            </div>
          </div>
          <div className="text-[12px] font-semibold text-slate-400">
            {visibleOrders.length} orders
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto">
          {ordersLoading ? (
            <div className="p-5 text-[13px] text-slate-500">
              Loading orders...
            </div>
          ) : ordersError ? (
            <div className="p-5 text-[13px] font-semibold text-rose-600">
              {ordersError}
            </div>
          ) : visibleOrders.length === 0 ? (
            <div className="p-5 text-[13px] text-slate-500">
              No warehouse orders found.
            </div>
          ) : (
            <div className="divide-y divide-[#f8dde1]">
              {visibleOrders.map((order) => {
                const active = order.id === selectedOrderId;
                return (
                  <button
                    key={order.id}
                    type="button"
                    onClick={() => setSelectedOrderId(order.id)}
                    className={
                      (active ? "bg-[#fff7f8]" : "bg-white hover:bg-slate-50") +
                      " w-full px-5 py-4 text-left transition"
                    }
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="text-[14px] font-bold text-slate-800">
                            {warehouseMap[order.warehouseId] || order.warehouseId}
                          </div>
                          <StatusPill value={order.status} />
                        </div>

                        <div className="mt-1 text-[12px] font-semibold text-slate-600">
                          Order ID: {getReadableWarehouseOrderId(order)}
                        </div>

                        <div className="mt-1 text-[12px] text-slate-500">
                          Items: {order.itemsCount || 0} • Pending: {order.pendingCount || 0}
                        </div>

                        {order.notes ? (
                          <div className="mt-2 text-[12px] text-slate-500">
                            {order.notes}
                          </div>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-2 text-[12px] font-semibold text-[#d86a78]">
                        <Eye size={14} /> View
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {pagination}
      </Card>

      <Card className="flex h-[760px] flex-col overflow-visible">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#f4d8dc] px-5 py-4">
          <div>
            <div className="text-[18px] font-bold text-slate-800">
              Order Product Decisions
            </div>
            {selectedOrder ? (
              <div className="mt-1 text-[12px] font-semibold text-slate-500">
                Review products, then save your decision from here.
              </div>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {selectedOrder ? <StatusPill value={selectedOrder.status} /> : null}
            {selectedOrder ? (
              <ActionButton
                onClick={submitDecisions}
                disabled={savingDecision || !selectedOrder.items?.length}
                className="shadow-sm"
              >
                <PackageCheck size={16} />
                {savingDecision
                  ? "Saving..."
                  : ["approved", "rejected", "partially_approved"].includes(
                    String(selectedOrder?.status || "").toLowerCase()
                  )
                    ? "Update Decision"
                    : "Save Decisions"}
              </ActionButton>
            ) : null}
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col p-5">
          {!selectedOrder ? (
            <div className="text-[13px] text-slate-500">
              Select any warehouse order to view product details.
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col gap-5">
              <div className="rounded-[16px] border border-[#f5dde1] bg-[#fffafb] p-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                      Warehouse
                    </div>
                    <div className="mt-1 text-[14px] font-bold text-slate-800">
                      {warehouseMap[selectedOrder.warehouseId] ||
                        selectedOrder.warehouseId}
                    </div>
                  </div>

                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                      Order ID
                    </div>
                    <div className="mt-1 text-[14px] font-bold text-slate-800">
                      {getReadableWarehouseOrderId(selectedOrder)}
                    </div>
                  </div>

                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                      Items
                    </div>
                    <div className="mt-1 text-[14px] font-bold text-slate-800">
                      {selectedOrder.items?.length || 0}
                    </div>
                  </div>

                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
                      Notes
                    </div>
                    <div className="mt-1 text-[13px] text-slate-700">
                      {selectedOrder.notes || "-"}
                    </div>
                  </div>
                </div>
              </div>

              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overflow-x-hidden pr-1">
                {(selectedOrder.items || []).map((item) => {
                  const lineKey = getOrderItemKey(item);
                  const current = decisions[lineKey] || {};
                  const isRejected = current.status === "rejected";
                  const variantLabel = getVariantLabel(item);

                  return (
                    <div
                      key={lineKey}
                      className="rounded-[18px] border border-[#f3d8dd] p-4"
                    >
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
                        <div>
                          <div className="break-words text-[15px] font-bold text-slate-800">
                            {item.productName || item.productId}
                          </div>
                          {variantLabel ? (
                            <div className="mt-1 text-[12px] font-semibold text-slate-500">
                              Variant: {variantLabel}
                            </div>
                          ) : null}
                          <div className="mt-3 flex flex-wrap items-center gap-2 text-[12px] text-slate-600">
                            <span className="rounded-full bg-slate-100 px-3 py-1">
                              Requested: {formatNumber(item.requestedQty)}
                            </span>
                            <StatusPill value={item.status} />
                          </div>
                        </div>

                        <SelectLike
                          value={current.status || "approved"}
                          onChange={(v) =>
                            updateDecision(lineKey, {
                              status: v,
                              ...(v === "rejected"
                                ? { approvedPurchasePrice: "", purchaseCurrency: "INR" }
                                : { purchaseCurrency: "INR" }),
                            })
                          }
                          placeholder="Decision"
                          options={[
                            { value: "approved", label: "Approve" },
                            { value: "rejected", label: "Reject" },
                          ]}
                        />

                        <input
                          type="number"
                          min="0"
                          value={current.approvedQty ?? item.requestedQty ?? ""}
                          disabled={isRejected}
                          onChange={(e) =>
                            updateDecision(lineKey, {
                              approvedQty: e.target.value,
                            })
                          }
                          required={!isRejected}
                          placeholder="Approved qty *"
                          className="h-[42px] rounded-[12px] border border-[#f2c6cc] px-4 text-[13px] outline-none transition focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4] disabled:cursor-not-allowed disabled:bg-slate-50"
                        />

                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={
                            current.approvedPurchasePrice ??
                            item.approvedPurchasePrice ??
                            ""
                          }
                          disabled={isRejected}
                          onChange={(e) =>
                            updateDecision(lineKey, {
                              approvedPurchasePrice: e.target.value,
                            })
                          }
                          required={!isRejected}
                          placeholder={isRejected ? "Purchase price" : "Purchase price *"}
                          className="h-[42px] rounded-[12px] border border-[#f2c6cc] px-4 text-[13px] outline-none transition focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4] disabled:cursor-not-allowed disabled:bg-slate-50"
                        />

                        <input
                          type="text"
                          value="INR"
                          readOnly
                          tabIndex={-1}
                          disabled={isRejected}
                          placeholder="INR"
                          className="h-[42px] cursor-not-allowed rounded-[12px] border border-[#f2c6cc] bg-slate-50 px-4 text-[13px] font-semibold uppercase text-slate-600 outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
                        />

                        <input
                          value={current.reason || ""}
                          onChange={(e) =>
                            updateDecision(lineKey, {
                              reason: e.target.value,
                            })
                          }
                          placeholder="Reject reason"
                          className="h-[42px] rounded-[12px] border border-[#f2c6cc] px-4 text-[13px] outline-none transition focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="sticky bottom-0 z-10 -mx-5 -mb-5 flex justify-end border-t border-[#f4d8dc] bg-white/95 px-5 py-4 shadow-[0_-10px_24px_rgba(15,23,42,0.06)] backdrop-blur">
                <ActionButton
                  onClick={submitDecisions}
                  disabled={savingDecision || !selectedOrder.items?.length}
                >
                  <PackageCheck size={16} />
                  {savingDecision
                    ? "Saving..."
                    : ["approved", "rejected", "partially_approved"].includes(
                      String(selectedOrder?.status || "").toLowerCase()
                    )
                      ? "Update Decision"
                      : "Save Decisions"}
                </ActionButton>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}